import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import UPNG from "upng-js";
import { fetchBakeAsset } from "@/services/nvgSimulationApi";
import type {
  NvgRendererHandle,
  SimulationPayload,
} from "@/types/nvgSimulation";

interface NvgRendererProps {
  brightnessScale?: number;
  frozen?: boolean;
}

const VERTEX_SHADER = /* glsl */ `
  varying vec2 vUv;
  void main() {
    // Flip V so image data (top-down) matches WebGL’s bottom-left texture origin
    vUv = vec2(uv.x, 1.0 - uv.y);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Physics-based shot noise + scintillation (white noise, pseudo-Gaussian, high-frequency grain)
const FRAGMENT_SHADER = /* glsl */ `
  precision highp float;

  uniform sampler2D u_opticalBase;
  uniform sampler2D u_prnuMap;
  uniform sampler2D u_distortionMap;
  uniform sampler2D u_colorLUT;
  uniform sampler2D u_circularMask;

  uniform float u_snr;
  uniform float u_ebiFlux;
  // u_noiseFactorSq is used to scale the noise intensity based on Gain/SNR
  uniform float u_noiseFactorSq;
  uniform float u_scintillationStrength;
  uniform float u_grainVisibility;
  uniform float u_grainScaleUV;
  uniform float u_grainAmplitude;

  uniform float u_time;
  uniform float u_brightnessScale;

  uniform int u_debugMode;

  varying vec2 vUv;

  vec3 lutLookup(float signal) {
    float lutU = (clamp(signal, 0.0, 1.0) * 255.0 + 0.5) / 256.0;
    return texture2D(u_colorLUT, vec2(lutU, 0.5)).rgb;
  }

  // --- NEW NOISE GENERATION ---

  // High-quality hash function (Gold Noise)
  // Much better than fract(sin(dot...)) which has artifacts
  float hashGold(vec2 xy) {
      return fract(tan(distance(xy * 1.61803398874989484820459, xy) * 1.41421356) * xy.x);
  }

  // Pseudo-Gaussian White Noise
  // Averaging two uniform randoms creates a "Triangle" distribution
  // which looks physically accurate (Bell curve) for Shot Noise.
  float gaussianWhiteNoise(vec2 uv, float seed) {
      float n1 = hashGold(uv + seed);
      float n2 = hashGold(uv + seed + 123.45);
      return (n1 + n2) * 0.5 - 0.5; // Range [-0.5, 0.5]
  }

  void main() {
    // 1. Geometric Distortion
    vec4 distPacked = texture2D(u_distortionMap, vUv);
    float xHi = floor(distPacked.r * 255.0 + 0.5);
    float xLo = floor(distPacked.g * 255.0 + 0.5);
    float yHi = floor(distPacked.b * 255.0 + 0.5);
    float yLo = floor(distPacked.a * 255.0 + 0.5);
    vec2 distortedUV = vec2(
      (xHi * 256.0 + xLo) / 65535.0,
      (yHi * 256.0 + yLo) / 65535.0
    );

    // Debug Pass-throughs
    if (u_debugMode == 2) { gl_FragColor = vec4(distortedUV, 0.0, 1.0); return; }
    if (u_debugMode == 4) { float m = texture2D(u_circularMask, vUv).r; gl_FragColor = vec4(m,m,m,1.0); return; }
    if (u_debugMode == 3) { float p = texture2D(u_prnuMap, vUv).r * 2.0; gl_FragColor = vec4(lutLookup(p), 1.0); return; }

    // 2. Optical Base Signal
    float signal = texture2D(u_opticalBase, distortedUV).r * 2.0;

    if (u_debugMode == 0) {
      signal *= u_brightnessScale;
    }

    if (u_debugMode == 1) { gl_FragColor = vec4(lutLookup(signal), 1.0); return; }

    // 3. PRNU (Fixed Pattern Noise / Chicken Wire)
    float prnu = texture2D(u_prnuMap, distortedUV).r * 2.0;
    signal *= prnu * u_grainVisibility + (1.0 - u_grainVisibility);

    // 4. PHYSICS-BASED SHOT NOISE & SCINTILLATION
    if (u_debugMode == 0) {
      float timeSeed = floor(u_time * 60.0) * 0.01;

      // A. SHOT NOISE (The "Sand")
      // We map the noise to the Distorted UVs but multiplied by a high frequency
      // to ensure it renders as pixels, not blobs.
      // Scaling by 2048.0 ensures fine grain regardless of texture resolution.
      vec2 noiseUV = distortedUV * 2048.0;

      float whiteNoise = gaussianWhiteNoise(noiseUV, timeSeed);

      // Physics: Shot noise variance is proportional to sqrt(signal).
      // Dark areas have MORE relative noise (low SNR), bright areas have less.
      // We add a tiny offset (0.001) to signal to avoid sqrt(0).
      float shotNoise = whiteNoise * sqrt(u_noiseFactorSq) * u_scintillationStrength * 0.05;

      // Apply noise. Note: We multiply by sqrt(signal) because photon statistics
      // follow a Poisson distribution.
      signal += shotNoise * sqrt(max(signal, 0.05));

      // B. SCINTILLATION (The "Popcorn" / Sparkles)
      // Occasional high-energy electrons striking the phosphor.
      // We use a separate, thresholded noise layer for this.
      float popNoise = hashGold(noiseUV + timeSeed + 42.0);

      // Only trigger bright sparks in very dark areas (EBI dominated)
      // or randomly across the tube.
      float scintillationThreshold = 0.995; // Top 0.5% of noise events
      if (popNoise > scintillationThreshold) {
         // Brightness of the spark
         float spark = (popNoise - scintillationThreshold) / (1.0 - scintillationThreshold);
         signal += spark * u_scintillationStrength * 0.5;
      }
    }

    // 5. EBI (Dark Current)
    signal += u_ebiFlux * 0.005;

    // 6. Color Grading
    vec3 phosphorColor = lutLookup(signal);

    // 7. Masking
    float mask = texture2D(u_circularMask, vUv).r;
    phosphorColor *= mask;

    gl_FragColor = vec4(phosphorColor, 1.0);
  }
`;

function createLuminance16Texture(buffer: ArrayBuffer): THREE.DataTexture {
  const img = UPNG.decode(buffer);
  const { width, height } = img;

  // PNG stores 16-bit values big-endian. Reconstruct and normalize to [0,1] as
  // float32 so we can use RedFormat+FloatType (gl.R32F), which is a valid WebGL2
  // sized internal format. RedFormat+UnsignedShortType maps to gl.RED as the
  // internal format, which WebGL2 rejects for non-UNSIGNED_BYTE types, producing
  // an all-zero texture that makes the rendered image completely black.
  const raw = new Uint8Array(img.data as unknown as ArrayBuffer);
  const floatPixels = new Float32Array(width * height);
  for (let i = 0; i < floatPixels.length; i++) {
    const hi = raw[i * 2] ?? 0;
    const lo = raw[i * 2 + 1] ?? 0;
    floatPixels[i] = ((hi << 8) | lo) / 65535.0;
  }

  const tex = new THREE.DataTexture(
    floatPixels,
    width,
    height,
    THREE.RedFormat,
    THREE.FloatType,
  );
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  return tex;
}

async function createBitmapTexture(
  buffer: ArrayBuffer,
  filter: THREE.MagnificationTextureFilter,
): Promise<THREE.Texture> {
  const blob = new Blob([buffer]);
  // premultiplyAlpha: 'none' is required for the distortion map, which packs
  // 16-bit UV coordinates into RGBA channels. The default 'default' behavior
  // premultiplies R/G/B by alpha, corrupting the packed UV data since the alpha
  // channel holds the low byte of the Y coordinate (not a real transparency value).
  const bitmap = await createImageBitmap(blob, { premultiplyAlpha: "none" });
  const tex = new THREE.Texture(bitmap as unknown as HTMLImageElement);
  tex.minFilter = filter;
  tex.magFilter = filter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  return tex;
}

const NvgRenderer = forwardRef<NvgRendererHandle, NvgRendererProps>(
  ({ brightnessScale = 1.0, frozen = false }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.OrthographicCamera | null>(null);
    const materialRef = useRef<THREE.ShaderMaterial | null>(null);
    const rafRef = useRef<number>(0);
    const frozenRef = useRef(frozen);
    const timeRef = useRef(0);
    const loadedTexturesRef = useRef<THREE.Texture[]>([]);
    const [hasSimulation, setHasSimulation] = useState(false);

    // Sync frozen prop to ref without re-render
    useEffect(() => {
      frozenRef.current = frozen;
    }, [frozen]);

    // Sync brightnessScale to uniform without re-render
    useEffect(() => {
      if (materialRef.current) {
        materialRef.current.uniforms["u_brightnessScale"]!.value =
          brightnessScale;
      }
    }, [brightnessScale]);

    // Mount: set up Three.js renderer, scene, camera, geometry, material, rAF loop
    useEffect(() => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      const renderer = new THREE.WebGLRenderer({
        canvas,
        alpha: false,
        antialias: false,
      });
      renderer.setSize(container.clientWidth, container.clientHeight, false);
      renderer.setPixelRatio(1);
      rendererRef.current = renderer;

      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
      cameraRef.current = camera;

      const scene = new THREE.Scene();
      sceneRef.current = scene;

      const geometry = new THREE.PlaneGeometry(2, 2);

      const material = new THREE.ShaderMaterial({
        vertexShader: VERTEX_SHADER,
        fragmentShader: FRAGMENT_SHADER,
        uniforms: {
          u_opticalBase: { value: null },
          u_prnuMap: { value: null },
          u_distortionMap: { value: null },
          u_colorLUT: { value: null },
          u_circularMask: { value: null },
          u_time: { value: 0.0 },
          u_brightnessScale: { value: brightnessScale },
          u_snr: { value: 30.0 },
          u_ebiFlux: { value: 2.0 },
          u_noiseFactorSq: { value: 4.0 },
          u_scintillationStrength: { value: 0.8 },
          u_grainVisibility: { value: 1.0 },
          u_grainScaleUV: { value: 256.0 },
          u_grainAmplitude: { value: 0.08 },
          u_debugMode: { value: 0 },
        },
      });
      materialRef.current = material;

      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      // ResizeObserver
      const ro = new ResizeObserver(() => {
        if (containerRef.current) {
          renderer.setSize(
            containerRef.current.clientWidth,
            containerRef.current.clientHeight,
            false,
          );
        }
      });
      ro.observe(container);

      // Animation loop
      let lastTime = performance.now();
      function animate(now: number) {
        rafRef.current = requestAnimationFrame(animate);
        const dt = (now - lastTime) / 1000;
        lastTime = now;
        if (!frozenRef.current) {
          timeRef.current += dt;
          material.uniforms["u_time"]!.value = timeRef.current;
        }
        renderer.render(scene, camera);
      }
      rafRef.current = requestAnimationFrame(animate);

      return () => {
        cancelAnimationFrame(rafRef.current);
        ro.disconnect();
        geometry.dispose();
        material.dispose();
        loadedTexturesRef.current.forEach((t) => t.dispose());
        renderer.dispose();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(ref, () => ({
      async loadSimulation(payload: SimulationPayload) {
        const { assets, shaderParams } = payload;

        // Fetch all 5 assets in parallel
        const [opticalBuf, prnuBuf, distortionBuf, lutBuf, maskBuf] =
          await Promise.all([
            fetchBakeAsset(assets.optical_base),
            fetchBakeAsset(assets.prnu_map),
            fetchBakeAsset(assets.distortion_map),
            fetchBakeAsset(assets.color_lut),
            fetchBakeAsset(assets.circular_mask),
          ]);

        // Decode 16-bit luminance textures
        const opticalTex = createLuminance16Texture(opticalBuf);
        const prnuTex = createLuminance16Texture(prnuBuf);

        // Bitmap textures — distortion map must use NearestFilter to preserve packed bytes exactly
        const distortionTex = await createBitmapTexture(
          distortionBuf,
          THREE.NearestFilter,
        );
        const lutTex = await createBitmapTexture(lutBuf, THREE.LinearFilter);
        const maskTex = await createBitmapTexture(maskBuf, THREE.LinearFilter);

        const newTextures = [
          opticalTex,
          prnuTex,
          distortionTex,
          lutTex,
          maskTex,
        ];

        // Dispose old textures
        loadedTexturesRef.current.forEach((t) => t.dispose());
        loadedTexturesRef.current = newTextures;

        const mat = materialRef.current;
        if (!mat) throw new Error("Material not initialized");

        mat.uniforms["u_opticalBase"]!.value = opticalTex;
        mat.uniforms["u_prnuMap"]!.value = prnuTex;
        mat.uniforms["u_distortionMap"]!.value = distortionTex;
        mat.uniforms["u_colorLUT"]!.value = lutTex;
        mat.uniforms["u_circularMask"]!.value = maskTex;

        mat.uniforms["u_snr"]!.value = shaderParams.snr;
        mat.uniforms["u_ebiFlux"]!.value = shaderParams.ebi_flux;
        mat.uniforms["u_noiseFactorSq"]!.value = shaderParams.noise_factor_sq;
        mat.uniforms["u_scintillationStrength"]!.value =
          shaderParams.scintillation_strength;
        mat.uniforms["u_grainVisibility"]!.value =
          shaderParams.grain_visibility;
        mat.uniforms["u_grainScaleUV"]!.value = shaderParams.grain_scale_uv;
        mat.uniforms["u_grainAmplitude"]!.value = shaderParams.grain_amplitude;

        setHasSimulation(true);
      },

      reset() {
        loadedTexturesRef.current.forEach((t) => t.dispose());
        loadedTexturesRef.current = [];
        setHasSimulation(false);
        const mat = materialRef.current;
        if (mat) {
          mat.uniforms["u_opticalBase"]!.value = null;
          mat.uniforms["u_prnuMap"]!.value = null;
          mat.uniforms["u_distortionMap"]!.value = null;
          mat.uniforms["u_colorLUT"]!.value = null;
          mat.uniforms["u_circularMask"]!.value = null;
        }
      },

      setDebugMode(mode: number) {
        const mat = materialRef.current;
        if (mat) {
          mat.uniforms["u_debugMode"]!.value = mode;
        }
      },
    }));

    return (
      <div
        ref={containerRef}
        className="relative w-full aspect-square bg-black rounded-lg overflow-hidden"
      >
        <canvas ref={canvasRef} className="w-full h-full" />
        {!hasSimulation && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-zinc-600 text-sm">No simulation loaded</p>
          </div>
        )}
      </div>
    );
  },
);

NvgRenderer.displayName = "NvgRenderer";

export { NvgRenderer };
